import React, { useState } from 'react';
import { Button, Input, Select, message, Tabs } from 'antd';
import { ClearOutlined, CheckOutlined, PlusOutlined, DeleteOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import wordClient from "../../client/WordClient"; // Import wordClient
import dictionaryClient from "../../client/DictionaryClient"; // Import dictionaryClient
import { englishPartsOfSpeech } from '../../data/englishPartsOfSpeech';
import {spanishPartsOfSpeech} from "../../data/spanishPartsOfSpeech";
import {polishPartsOfSpeech} from "../../data/polishPartsOfSpeech";
import {chinesePartsOfSpeech} from "../../data/chinesePartsOfSpeech";
import {germanPartsOfSpeech} from "../../data/germanPartsOfSpeech";
import useTranslation from "../../hooks/useTranslation";

const { TabPane } = Tabs;

/**
 * Ключ вкладки части речи. Позиция ключом быть не может: удаление вкладки сдвигает
 * все следующие, и antd вместе с React переносят активную вкладку, фокус и введённый
 * текст на соседнюю запись. Счётчик живёт на модуль — ключи нужны уникальными только
 * в пределах одного открытого списка.
 */
let entrySequence = 0;
const nextEntryKey = () => {
    entrySequence += 1;
    return `pos-${entrySequence}`;
};

// Определения и примеры тоже получают ключ при создании: индекс в ключе годился, пока
// списки только росли с конца, но ключ не должен зависеть от этого свойства формы.
const emptyExample = () => ({ key: nextEntryKey(), sentence: '', translation: '' });
const emptyDefinition = () => ({ key: nextEntryKey(), definition: '', translation: '', examples: [emptyExample()] });

const ContentAddWord = () => {
    const { t } = useTranslation();
    const selectedDeck = useSelector((state) => state.deck.selectedDeck);
    const deckId = selectedDeck ? selectedDeck.deckId : null;
    const { locale } = useSelector((state) => state.deck.selectedDeck);
    const { translateLocale } = useSelector((state) => state.deck.selectedDeck);

    const getPartsOfSpeechOptions = () => {
        switch (locale.code) {
            case 'GERMAN':
                return germanPartsOfSpeech;
            case 'CHINESE_SIMPLIFIED':
                return chinesePartsOfSpeech;
            case 'POLISH':
                return polishPartsOfSpeech;
            case 'SPANISH_ES':
                return spanishPartsOfSpeech;
            default:
                return englishPartsOfSpeech; // Default to English if no locale matches
        }
    };

    // Determine if the transcription or pinyin input should be shown based on locale
    const shouldShowPhonetic = ['ENGLISH_GB', 'ENGLISH_US', 'CHINESE_SIMPLIFIED', 'CHINESE_TRADITIONAL'].includes(locale.code);


    const [word, setWord] = useState('');
    const [partOfSpeechEntries, setPartOfSpeechEntries] = useState([
        {
            key: nextEntryKey(),
            partOfSpeech: '',
            word: '',
            phonetic: '',
            definitionsTranslations: [
                emptyDefinition(),
            ],
        },
    ]); // Initial state with one tab
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);

    const handleDefinitionTranslationChange = (tabIndex, index, field, value) => {
        const newPartOfSpeechEntries = [...partOfSpeechEntries];
        newPartOfSpeechEntries[tabIndex].definitionsTranslations[index][field] = value;
        setPartOfSpeechEntries(newPartOfSpeechEntries);
    };

    const handleAddDefinitionTranslation = (tabIndex) => {
        const newPartOfSpeechEntries = [...partOfSpeechEntries];
        newPartOfSpeechEntries[tabIndex].definitionsTranslations.push(emptyDefinition());
        setPartOfSpeechEntries(newPartOfSpeechEntries);
    };

    const handleDeleteLastDefinitionTranslation = (tabIndex) => {
        const newPartOfSpeechEntries = [...partOfSpeechEntries];
        if (newPartOfSpeechEntries[tabIndex].definitionsTranslations.length > 1) {
            newPartOfSpeechEntries[tabIndex].definitionsTranslations.pop();
            setPartOfSpeechEntries(newPartOfSpeechEntries);
        }
    };

    const handleAddExample = (tabIndex, definitionIndex) => {
        const newPartOfSpeechEntries = [...partOfSpeechEntries];
        newPartOfSpeechEntries[tabIndex].definitionsTranslations[definitionIndex].examples.push(emptyExample());
        setPartOfSpeechEntries(newPartOfSpeechEntries);
    };

    const handleDeleteExample = (tabIndex, definitionIndex) => {
        const newPartOfSpeechEntries = [...partOfSpeechEntries];
        if (newPartOfSpeechEntries[tabIndex].definitionsTranslations[definitionIndex].examples.length > 1) {
            newPartOfSpeechEntries[tabIndex].definitionsTranslations[definitionIndex].examples.pop();
            setPartOfSpeechEntries(newPartOfSpeechEntries);
        }
    };

    const handleExampleChange = (tabIndex, definitionIndex, exampleIndex, field, value) => {
        const newPartOfSpeechEntries = [...partOfSpeechEntries];
        newPartOfSpeechEntries[tabIndex].definitionsTranslations[definitionIndex].examples[exampleIndex][field] = value;
        setPartOfSpeechEntries(newPartOfSpeechEntries);
    };

    const handleClearForm = () => {
        setWord('');
        setPartOfSpeechEntries([
            {
                key: nextEntryKey(),
                partOfSpeech: '',
                word: '',
                phonetic: '',
                definitionsTranslations: [
                    emptyDefinition(),
                ],
            },
        ]);
    };

    const handleSearch = async () => {
        console.log("Locale:", locale); // Check if locale is defined
        console.log("translateLocale:", translateLocale);
        console.log("Word:", word);     // Check if word is defined
        setSearching(true);
        try {
            message.info(t('word.searching'));
            const response = await dictionaryClient.getWord(word,locale.code,translateLocale.code);



            if (response && response.length > 0) {
                const newEntries = response.map((entry) => ({
                    key: nextEntryKey(),
                    partOfSpeech: entry.partOfSpeech,
                    word: entry.headword,
                    dictionaryWordId: entry.dictionaryWordId,


                    phonetic: (() => {
                        return locale.code.startsWith('ENGLISH') || locale.code.startsWith('CHINESE')
                            ? entry.phonetic
                            : '';
                    })(),

                    definitionsTranslations: entry.wordDefinition.map((def) => ({
                        key: nextEntryKey(),
                        id: def.id,
                        definition: def.definition,
                        translation: def.translation,
                        examples: def.examples.map((ex) => ({
                            key: nextEntryKey(),
                            id: ex.id,
                            sentence: ex.sentence,
                            translation: ex.translation,
                        })),
                    })),
                }));
                setPartOfSpeechEntries(newEntries);
            } else {
                message.warning(t('word.noResults'));
            }
        } catch (error) {
            console.error('Error fetching word data:', error);
            message.error(t('word.searchFailed'));
        } finally {
            setSearching(false);
        }
    };

    const handleSave = async () => {
        if (!deckId) {
            message.error(t('deck.noneSelected'));
            return;
        }

        for (const entry of partOfSpeechEntries) {
            if (!entry.word.trim()) {
                message.error(t('word.wordRequired'));
                return;
            }

            if (shouldShowPhonetic && !entry.phonetic.trim()) {
                message.error(t('word.phoneticRequired'));
                return;
            }

            if (!entry.partOfSpeech.trim()) {
                message.error(t('word.partOfSpeechRequired'));
                return;
            }
        }

        const wordDataArray = partOfSpeechEntries.map((entry) => ({
            deckId: deckId,
            // Слово из словаря помнит свою статью; введённое вручную живёт без неё.
            canonicalWordId: entry.dictionaryWordId || null,
            headword: entry.word,
            phonetic: entry.phonetic,
            partOfSpeech: entry.partOfSpeech,
            wordDefinition: entry.definitionsTranslations.map((item) => ({
                id: item.id,
                definition: item.definition,
                translation: item.translation,
                examples: item.examples.map((ex) => ({
                    id: ex.id,
                    sentence: ex.sentence,
                    translation: ex.translation,
                })),
            })),
        }));

        setLoading(true);
        try {
            for (const wordData of wordDataArray) {
                await wordClient.createWord(wordData);
            }
            message.success(t('word.added'));
            handleClearForm();
        } catch (error) {
            console.error('Error saving the words:', error);
            message.error(t('word.addFailed', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveTab = (tabIndex) => {
        const newEntries = [...partOfSpeechEntries];
        newEntries.splice(tabIndex, 1);
        setPartOfSpeechEntries(newEntries);
    };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
            <div style={{ border: '1px solid #d9d9d9', padding: '16px', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <Input
                        placeholder={t('word.wordPlaceholder')}
                        value={word}
                        onChange={(e) => setWord(e.target.value)}
                        style={{ flex: 1 }}
                    />
                    <Button
                        icon={<SearchOutlined />}
                        onClick={handleSearch}
                        loading={searching}
                    />
                </div>
            </div>

            <Tabs
                tabBarStyle={partOfSpeechEntries.length <= 1 ? { display: 'none' } : {}}
            >
                {partOfSpeechEntries.map((entry, tabIndex) => (
                    <TabPane
                        tab={
                            <span>
                                {entry.partOfSpeech || t('word.newEntry')}
                                {partOfSpeechEntries.length > 1 && (
                                    <CloseOutlined
                                        onClick={() => handleRemoveTab(tabIndex)}
                                        style={{ fontSize: '10px', marginLeft: 8 }} // Adjust font size here
                                    />
                                )}
                            </span>
                        }
                        key={entry.key}
                    >
                        <div
                            style={{
                                border: '1px solid #d9d9d9',
                                padding: '16px',
                                borderRadius: partOfSpeechEntries.length > 1 ? '0 0 8px 8px' : '8px',
                                borderTopLeftRadius: partOfSpeechEntries.length > 1 ? '0px' : '8px',
                                borderTopRightRadius: partOfSpeechEntries.length > 1 ? '0px' : '8px',
                                borderTop: partOfSpeechEntries.length > 1 ? '0px' : '1px solid #d9d9d9'
                            }}
                        >


                            <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px'}}>


                                <Select
                                    showSearch
                                    placeholder={t('word.partOfSpeechPlaceholder')}
                                    value={entry.partOfSpeech}
                                    onChange={(value) => {
                                        const newEntries = [...partOfSpeechEntries];
                                        newEntries[tabIndex].partOfSpeech = value;
                                        setPartOfSpeechEntries(newEntries);
                                    }}
                                    style={{width: '150px'}}
                                    options={getPartsOfSpeechOptions()}
                                />
                            </div>



                            <div style={{ marginBottom: '16px' }}>
                                {shouldShowPhonetic && (
                                    <Input
                                        placeholder={t('word.phoneticPlaceholder')}
                                        value={entry.phonetic}
                                        onChange={(e) => {
                                            const newEntries = [...partOfSpeechEntries];
                                            newEntries[tabIndex].phonetic = e.target.value;
                                            setPartOfSpeechEntries(newEntries);
                                        }}
                                    />
                                )}


                            </div>







                            {entry.definitionsTranslations.map((item, index) => (
                                <div
                                    key={item.key}
                                    style={{
                                        border: '1px solid #d9d9d9',
                                        padding: '16px',
                                        borderRadius: '8px',
                                        marginBottom: '16px',
                                    }}
                                >
                                    <Input
                                        style={{marginBottom: '6px'}}
                                        placeholder={t('word.definitionPlaceholder', { number: index + 1 })}
                                        value={item.definition}
                                        onChange={(e) => handleDefinitionTranslationChange(tabIndex, index, 'definition', e.target.value)}
                                    />
                                    <Input
                                        placeholder={t('word.definitionTranslationPlaceholder', { number: index + 1 })}
                                        value={item.translation}
                                        onChange={(e) => handleDefinitionTranslationChange(tabIndex, index, 'translation', e.target.value)}
                                        style={{marginBottom: '12px'}}
                                    />
                                    {item.examples.map((example, exampleIndex) => (
                                        <div key={example.key} style={{marginBottom: '12px', paddingLeft: '16px'}}>
                                            <Input
                                                style={{marginBottom: '6px'}}
                                                placeholder={t('word.examplePlaceholder', { number: exampleIndex + 1 })}
                                                value={example.sentence}
                                                onChange={(e) => handleExampleChange(tabIndex, index, exampleIndex, 'sentence', e.target.value)}
                                            />
                                            <Input
                                                placeholder={t('word.exampleTranslationPlaceholder', { number: exampleIndex + 1 })}
                                                value={example.translation}
                                                onChange={(e) => handleExampleChange(tabIndex, index, exampleIndex, 'translation', e.target.value)}
                                            />
                                        </div>
                                    ))}
                                    <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '12px'}}>
                                        <Button type="text" size="small"
                                                onClick={() => handleAddExample(tabIndex, index)}>
                                            {t('word.addExample')}
                                        </Button>
                                        {item.examples.length > 1 && (
                                            <Button type="text" size="small" icon={<DeleteOutlined/>}
                                                    onClick={() => handleDeleteExample(tabIndex, index)}>
                                                {t('common.delete')}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <div style={{display: 'flex', justifyContent: 'space-between', gap: '16px'}}>
                                <div>
                                    <Button
                                        icon={<PlusOutlined/>}
                                        onClick={() => handleAddDefinitionTranslation(tabIndex)}
                                        style={{marginRight: '8px'}}
                                    >
                                        {t('word.definition')}
                                    </Button>
                                    {entry.definitionsTranslations.length > 1 && (
                                        <Button
                                            icon={<DeleteOutlined/>}
                                            danger
                                            onClick={() => handleDeleteLastDefinitionTranslation(tabIndex)}
                                        >
                                            {t('word.definition')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </TabPane>
                ))}
            </Tabs>

            <div style={{display: 'flex', justifyContent: 'space-between', gap: '16px'}}>
                <div style={{display: 'flex', gap: '16px'}}>
                    <Button
                        icon={<ClearOutlined/>}
                        onClick={handleClearForm}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        loading={loading}
                        onClick={handleSave} // Call the handleSave function here
                    >
                        {t('common.save')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ContentAddWord;
